import { isEmpty, take } from 'lodash';
import { useMemo } from 'react';
import { DynamicTooltipChip } from 'src/components/dynamicTooltipChip/DynamicTooltipChip';
import { FullSizeAssemblyScreen } from 'src/components/fullSizeAssemblyScreen';
import { FullSizeAssemblySection } from 'src/components/fullSizeAssemblySection/FullSizeAssemblySection';
import { generateGridLayout } from 'src/components/generateGridLayout/GenerateGridLayout';
import { AssemblyJourneys } from 'src/constants/assemblyJourneys';
import { IAssemblyComponent, ILayout } from 'src/interfaces';
import { lang } from 'src/utils';
import { GcfMockData } from 'src/utils/gcf-helper.js';

import {
  IDefaultSection,
  IGridSection,
  IScheduledSectionComponent,
} from '../../ScheduleComponentPage.interface';
import { formatAssemblyComponents } from '../layoutScreenBuilder/LayoutScreenBuilder.helper';
import { getDynamicSections } from './LayoutScreens.helper';
import { ScreenWrapper } from './LayoutScreens.styled';

export interface ILayoutScreens {
  layout: ILayout | undefined;
  customerState: Partial<GcfMockData>;
  activeAssemblyJourney: AssemblyJourneys | undefined;
  isCodConfirmationOpen: boolean | undefined;
  scale?: number;
  layerScreenSections: Record<number, (IGridSection | IDefaultSection)[]>;
  currentPositionGridLines: string[];
  isAbleToEditSection?: (
    components: IScheduledSectionComponent[] | undefined,
  ) => boolean;
  handleSectionClick?: (screenIndex: number, sectionIndex: number) => void;
}

export const LayoutScreens = (props: ILayoutScreens) => {
  const {
    layout,
    customerState,
    activeAssemblyJourney,
    isCodConfirmationOpen,
    layerScreenSections,
    currentPositionGridLines,
  } = props;

  const onSectionClick = (screenIndex: number, sectionIndex: number) => () => {
    props.handleSectionClick?.(screenIndex, sectionIndex);
  };

  const sectionTypes = useMemo(
    () => layout?.config?.sectionTypes ?? [],
    [layout?.config?.sectionTypes],
  );

  // calculate the correct zoom level for the tooltip chip based on the number of screens in the layout
  const layoutScreenCount = layout?.screens.length ?? 0;
  const dynamicTooltipChipZoom = Math.max(3, layoutScreenCount);

  return (
    <>
      {layout?.screens.map((screen, index) => {
        const dynamicSections = getDynamicSections(screen.sections);

        return (
          <ScreenWrapper
            key={screen.layoutScreenId}
            sx={{
              position: 'relative',
              padding: '240px 0 0',
            }}>
            <DynamicTooltipChip
              chipName={`${lang.screen} ${index + 1}`}
              sx={{
                zoom: dynamicTooltipChipZoom,
                position: 'absolute',
                top: 0,
              }}
            />
            <FullSizeAssemblyScreen
              key={screen.name + index}
              height={screen.height}
              width={screen.width}
              selectable={false}
              type="screen">
              {currentPositionGridLines?.[index] &&
                generateGridLayout(currentPositionGridLines[index], 1, false)}
              {dynamicSections.map((section, index) => (
                <FullSizeAssemblySection
                  sectionTypes={sectionTypes}
                  key={section.name + index}
                  section={section}
                  customerState={customerState}
                  activeAssemblyJourney={activeAssemblyJourney}
                  isCodConfirmationOpen={isCodConfirmationOpen}
                />
              ))}

              {layerScreenSections?.[index]?.map((section, sectionIndex) => {
                if (
                  (section.isDisclaimer || section.isDYR) &&
                  isEmpty(section.components)
                )
                  return null;

                const hasAllowOverrides =
                  props.isAbleToEditSection?.(section.components) ?? false;

                const formattedComponents = take(section.components)?.map(
                  (item) => {
                    return {
                      ...item,
                      ...(item.override && { ...item.override }),
                    } as IAssemblyComponent;
                  },
                );
                const assemblyComponents =
                  formatAssemblyComponents(formattedComponents);

                return (
                  <FullSizeAssemblySection
                    sectionTypes={layout?.config?.sectionTypes}
                    key={section.i}
                    section={{
                      id: -1,
                      name: section.name!,
                      width: section.width!,
                      height: section.height!,
                      left_offset: section.leftOffset!,
                      top_offset: section.topOffset!,
                      created_at: '',
                      modified_at: '',
                    }}
                    showSectionBorder={!section.components?.length}
                    isLayoutList={!section.components?.length}
                    assemblyComponents={assemblyComponents}
                    showEditIcon={hasAllowOverrides}
                    selectable={hasAllowOverrides}
                    onClick={onSectionClick(index, sectionIndex)}
                  />
                );
              })}
            </FullSizeAssemblyScreen>
          </ScreenWrapper>
        );
      })}
    </>
  );
};
